import React from "react"
import { useTranslation } from "react-i18next"
import Skeleton from "react-loading-skeleton"

import classNames from "classnames"

import { ErrorPage } from "shared/ui/error-page/ErrorPage"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Offcanvas } from "shared/ui/offcanvas/Offcanvas"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { CreateProjectElementButton } from "features/project-management/ui/create-project-element-button/CreateProjectElementButton"
import { ActionsButton } from "widgets/actions-button/ActionsButton"
import { ProductPositionTable } from "widgets/product-position-table/ProductPositionTable"
import { ProductPositionTableHeader } from "widgets/product-position-table/ProductPositionTableHeader"
import { ProjectHeader } from "widgets/project-header/ProjectHeader"

import { ProjectPageDnDProvider } from "./context/ProjectPageDnDProvider"
import { useProjectPageViewModel } from "./hooks/useProjectPageViewModel"
import "./styles/_project-page.scss"
import { ProjectPageTabBar } from "./ui/project-page-tab-bar/ProjectPageTabBar"

export type ProjectPageViewModel = ReturnType<typeof useProjectPageViewModel>

function ProjectPageViewComponent({
  projectModel,
  RowRenderer,
  tableAPIRef,
  isTableLoading,
  isItemCollapsed,
  isSortingDisabled,
  noDataHint,
  onPositionChange,
  onItemCollapse,
  onSelect,
  pendingProjectChildren,
  selectedElementIDs,
  sidebar,

  chatContext,
  hideChat,
  chatWidgets,

  hideQuantity,
  hideActions,
  onActions,

  actionsButtonLabel,

  additionalActions,

  floors,
  activeTabID,
  onTabSelect,
  unassignedRoomsCount,
  unassignedProductsCount,
  visibleChildren,
  showClarificationRequestColumn,
  onSelectFloor,
  onFloorSort,
  onDirectDropIntoFloor,
  FloorTabRenderer,

  onCollapseAll,
  onExpandAll,

  showSelectionCheckbox,

  hideTabBar,
  className
}: ProjectPageViewModel) {
  const { t } = useTranslation()

  const project = projectModel?.core.data.project?.data
  const permissions = projectModel?.core.data.permissions?.data
  const isLoading = projectModel?.core.data.project?.isLoading
  const isLoadingPermissions = projectModel?.core.data.permissions?.isLoading
  const error = projectModel?.core.data.project?.error

  if (error) {
    return <ErrorPage />
  }

  const isReadonly =
    !isLoadingPermissions &&
    permissions &&
    !permissions[ProjectPermissionType.EDIT]

  return (
    <ProjectPageDnDProvider>
      <div
        id="project-page"
        className={classNames("project-page", className)}
        data-readonly={isReadonly ? "" : undefined}
      >
        <div className="project-page__header">
          {project?.id === FAVORITES_PROJECT_ID ? (
            <h1 className="h3 project-page__simple-header">
              {project?.title ? project.title : <Skeleton />}
            </h1>
          ) : (
            <ProjectHeader
              projectModel={projectModel}
              isLoading={isLoading}
              additionalActions={additionalActions}
              onClick={projectModel?.core.handlers.onShowDetails}
            />
          )}
        </div>

        {hideTabBar ? null : (
          <div className="project-page__tab-bar">
            {isReadonly ? null : (
              <CreateProjectElementButton
                onNewProduct={
                  projectModel?.creation?.handlers.onNewProduct ?? undefined
                }
                onNewRoom={
                  projectModel?.creation?.handlers.onNewRoom ?? undefined
                }
                onNewFloor={
                  projectModel?.creation?.handlers.onNewFloor ?? undefined
                }
                className="project-page__create-element-button"
              />
            )}
            <ProjectPageTabBar
              projectID={project?.id ?? ""}
              floors={floors}
              activeTabID={activeTabID}
              onSelect={onTabSelect}
              unassignedRoomsCount={unassignedRoomsCount}
              unassignedProductsCount={unassignedProductsCount}
              onSelectFloor={onSelectFloor}
              onFloorSort={onFloorSort}
              onDrop={onDirectDropIntoFloor}
              ariaControls="project-page-table"
              FloorTabRenderer={FloorTabRenderer}
            />
          </div>
        )}

        <div
          className="project-page__table card card--elevated"
          id="project-page-table"
        >
          <div className="project-page__table-header">
            <ProductPositionTableHeader
              hideQuantity={hideQuantity}
              showClarificationRequestColumn={showClarificationRequestColumn}
              showSelectionCheckbox={showSelectionCheckbox}
              onCollapseAll={onCollapseAll}
              onExpandAll={onExpandAll}
            />
          </div>
          <ProductPositionTable
            key={`${activeTabID}-product-position-table`}
            projectID={project?.id}
            activeTabID={activeTabID}
            RowRenderer={RowRenderer}
            tableAPIRef={tableAPIRef}
            projectChildren={visibleChildren}
            selectedItems={selectedElementIDs}
            isItemCollapsed={isItemCollapsed}
            onItemCollapse={onItemCollapse}
            isLoading={isTableLoading}
            hideQuantity={hideQuantity}
            showClarificationRequestColumn={showClarificationRequestColumn}
            showSelectionCheckbox={showSelectionCheckbox}
            onSelectionChange={onSelect}
            isSortingDisabled={isSortingDisabled}
            onPositionChange={onPositionChange}
            noDataHint={noDataHint}
            pendingProjectChildren={pendingProjectChildren}
            maxDepth={10}
          />
        </div>

        <div className="project-page__sidebar">{sidebar}</div>

        {!hideActions && onActions ? (
          <ActionsButton
            icon={IconName.KEBAB}
            onClick={onActions}
            label={actionsButtonLabel || t("General.Actions")}
            data-testid="project__actions"
          />
        ) : null}
        {!hideChat ? (
          <Offcanvas
            className="project-page-offcanvas light"
            show={chatContext.isVisible}
            onHide={chatContext.hide}
            placement="end"
            scroll
          >
            <div className={classNames("project-page-offcanvas__widgets")}>
              {chatWidgets}
            </div>
          </Offcanvas>
        ) : null}
      </div>
    </ProjectPageDnDProvider>
  )
}

export const ProjectPageView = React.memo(
  ProjectPageViewComponent
) as typeof ProjectPageViewComponent
